import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaBriefcase,
  FaCalendarAlt,
  FaEdit,
  FaImages,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaProjectDiagram,
  FaStickyNote,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import PortalDomainSelect from "./PortalDomainSelect";
import PortalAddressFields, {
  addressFormFromRow,
  addressPayloadFromForm,
} from "./PortalAddressFields";
import PortalEntityAttachmentsModal from "./PortalEntityAttachmentsModal";
import PortalAlert from "./PortalAlert";
import PortalConfirmModal from "./PortalConfirmModal";
import { canDeleteInPortal, isModuleEnabled } from "./portalSession";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { stateShortName } from "../utils/stateCodes";
import PortalSelect from "./PortalSelect";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";

const CLIENT_STATUSES = ["Active", "Inactive"];
const SERVICE_STATUSES = ["Active", "Inactive"];
const PROJECT_STATUSES = ["Active", "Inactive"];

const EMPTY_SERVICE_FORM = {
  serviceName: "",
  description: "",
  status: "Active",
};
const EMPTY_PROJECT_FORM = {
  projectName: "",
  shortCode: "",
  description: "",
  address: "",
  totalBeds: "",
  availableBeds: "",
  rentRange: "",
  projectUrl: "",
  amenities: [],
  amenityDraft: "",
  status: "Active",
};

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function joinParts(parts, separator) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(separator);
}

function EmailLink({ email }) {
  const value = String(email || "").trim();
  if (!value) return "—";
  return (
    <a href={`mailto:${value}`} className="cportal-contact-link">
      {value}
    </a>
  );
}

function PhoneLink({ phone }) {
  const value = String(phone || "").trim();
  if (!value) return "—";
  return (
    <a href={`tel:${value.replace(/[^\d+]/g, "")}`} className="cportal-contact-link">
      {value}
    </a>
  );
}

function WebsiteLink({ url }) {
  const value = String(url || "").trim();
  if (!value) return "—";
  const href = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="cportal-contact-link">
      {value}
    </a>
  );
}

function DetailSection({ icon: Icon, title, children, className = "", actions = null }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <div className="cportal-detail-section-head">
        <h2 className="cportal-detail-section-title">
          {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function clientInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

const BUSINESS_SUB_TYPES = ["Project", "Service", "Both"];

function toForm(row) {
  return {
    clientName: row?.Client_Name || "",
    contactPerson: row?.Contact_Person || "",
    mobileNumber: row?.Mobile_Number || "",
    altNumber: row?.Alt_Number || "",
    email: row?.Email || "",
    ...addressFormFromRow(row),
    // Clients saved before structured address only have the free-text Address.
    ...(!row?.Address_Line_1 && row?.Address ? { addressLine1: row.Address } : null),
    website: row?.Website || "",
    domain: row?.Domain || "",
    businessSubType: row?.Business_Sub_Type || "",
    description: row?.Description || "",
    status: row?.Status || "Active",
  };
}

function buildPayload(form) {
  return {
    Client_Name: form.clientName.trim(),
    Contact_Person: form.contactPerson.trim() || null,
    Mobile_Number: form.mobileNumber.trim() || null,
    Alt_Number: form.altNumber.trim() || null,
    Email: form.email.trim() || null,
    ...addressPayloadFromForm(form),
    Website: form.website.trim() || null,
    Domain: form.domain.trim(),
    Business_Sub_Type: form.businessSubType.trim(),
    Description: form.description.trim() || null,
    Status: form.status || "Active",
  };
}

function toServiceForm(row) {
  return {
    serviceName: row?.Service_Name || "",
    description: row?.Description || "",
    status: row?.Status || "Active",
  };
}

function buildServicePayload(form) {
  return {
    Service_Name: form.serviceName.trim(),
    Description: form.description.trim() || null,
    Status: form.status || "Active",
  };
}

function toProjectForm(row) {
  const amenities = Array.isArray(row?.Amenities)
    ? row.Amenities.filter(Boolean).map((item) => String(item).trim()).filter(Boolean)
    : [];
  return {
    projectName: row?.Project_Name || "",
    shortCode: row?.Short_Code || "",
    description: row?.Description || "",
    address: row?.Address || "",
    totalBeds: row?.Total_Beds != null ? String(row.Total_Beds) : "",
    availableBeds: row?.Available_Beds != null ? String(row.Available_Beds) : "",
    rentRange: row?.Rent_Range || "",
    projectUrl: row?.Project_URL || "",
    amenities,
    amenityDraft: "",
    status: row?.Status || "Active",
  };
}

function buildProjectPayload(form) {
  const parseBeds = (value) => {
    if (value === "" || value == null) return null;
    const n = Number.parseInt(String(value).trim(), 10);
    return Number.isNaN(n) ? null : n;
  };
  return {
    Project_Name: form.projectName.trim(),
    Short_Code: form.shortCode.trim(),
    Description: form.description.trim() || null,
    Address: form.address.trim() || null,
    Total_Beds: parseBeds(form.totalBeds),
    Available_Beds: parseBeds(form.availableBeds),
    Rent_Range: form.rentRange.trim() || null,
    Project_URL: (form.projectUrl || "").trim() || null,
    Amenities: Array.isArray(form.amenities)
      ? form.amenities.map((item) => String(item).trim()).filter(Boolean)
      : [],
    Status: form.status || "Active",
  };
}

function TenantClientView({ modules = null }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE_FORM);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [serviceSubmitting, setServiceSubmitting] = useState(false);
  const [serviceFormError, setServiceFormError] = useState("");
  const [deactivatingServiceId, setDeactivatingServiceId] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT_FORM);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectSubmitting, setProjectSubmitting] = useState(false);
  const [projectFormError, setProjectFormError] = useState("");
  const [deactivatingProjectId, setDeactivatingProjectId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [activeSection, setActiveSection] = useState("details");
  const [attachmentsTarget, setAttachmentsTarget] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);
  const showProjectsTab =
    isModuleEnabled(modules, "projects") &&
    (!client?.Business_Sub_Type ||
      client.Business_Sub_Type === "Project" ||
      client.Business_Sub_Type === "Both");
  const showServicesTab =
    isModuleEnabled(modules, "services") &&
    (!client?.Business_Sub_Type ||
      client.Business_Sub_Type === "Service" ||
      client.Business_Sub_Type === "Both");

  useEffect(() => {
    if (activeSection === "projects" && !showProjectsTab) setActiveSection("details");
    if (activeSection === "services" && !showServicesTab) setActiveSection("details");
  }, [activeSection, showProjectsTab, showServicesTab]);

  const loadClient = useCallback(async () => {
    const res = await axiosInstance.get(`/client/tenant-clients/${id}`);
    const data = res.data?.data || null;
    setClient(data);
    setServices(Array.isArray(data?.Services) ? data.Services : []);
    setProjects(Array.isArray(data?.Projects) ? data.Projects : []);
    setForm(toForm(data));
    return data;
  }, [id]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await loadClient();
        if (!active) return;
        if (!data) setError("Client not found.");
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load client details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadClient]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const setServiceField = (name, value) => setServiceForm((f) => ({ ...f, [name]: value }));
  const setProjectField = (name, value) => setProjectForm((f) => ({ ...f, [name]: value }));

  const closeServiceModal = () => {
    setShowServiceModal(false);
    setEditingServiceId(null);
    setServiceForm(EMPTY_SERVICE_FORM);
    setServiceFormError("");
  };

  const openAddServiceModal = () => {
    setEditingServiceId(null);
    setServiceForm(EMPTY_SERVICE_FORM);
    setServiceFormError("");
    setShowServiceModal(true);
  };

  const openEditServiceModal = (service) => {
    setEditingServiceId(service.Tenant_Client_Service_Id);
    setServiceForm(toServiceForm(service));
    setServiceFormError("");
    setShowServiceModal(true);
  };

  const closeProjectModal = () => {
    setShowProjectModal(false);
    setEditingProjectId(null);
    setProjectForm(EMPTY_PROJECT_FORM);
    setProjectFormError("");
  };

  const openAddProjectModal = () => {
    setEditingProjectId(null);
    setProjectForm(EMPTY_PROJECT_FORM);
    setProjectFormError("");
    setShowProjectModal(true);
  };

  const openEditProjectModal = (project) => {
    setEditingProjectId(project.Tenant_Project_Id);
    setProjectForm(toProjectForm(project));
    setProjectFormError("");
    setShowProjectModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.clientName.trim()) {
      setMessage({ type: "error", text: "Client name is required." });
      return;
    }
    if (!form.contactPerson.trim()) {
      setMessage({ type: "error", text: "Contact person is required." });
      return;
    }
    if (!form.domain.trim()) {
      setMessage({ type: "error", text: "Business Type is required." });
      return;
    }
    if (!BUSINESS_SUB_TYPES.includes(form.businessSubType)) {
      setMessage({ type: "error", text: "Business Sub Type is required." });
      return;
    }

    const mobileError = mobileValidationMessage(form.mobileNumber, {
      required: true,
      label: "Mobile number",
    });
    if (mobileError) {
      setMessage({ type: "error", text: mobileError });
      return;
    }
    const altNumberError = mobileValidationMessage(form.altNumber, { label: "Alternate number" });
    if (altNumberError) {
      setMessage({ type: "error", text: altNumberError });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/tenant-clients/${id}`, buildPayload(form));
      const data = res.data?.data;
      if (data) {
        setClient(data);
        setServices(Array.isArray(data.Services) ? data.Services : []);
        setProjects(Array.isArray(data.Projects) ? data.Projects : []);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Client updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update client.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setConfirmTarget({ kind: "client" });
  };

  const closeConfirm = () => {
    if (deleting || deactivatingServiceId || deactivatingProjectId) return;
    setConfirmTarget(null);
  };

  const confirmDestructive = async () => {
    if (!confirmTarget) return;

    if (confirmTarget.kind === "client") {
      setDeleting(true);
      setMessage({ type: "", text: "" });
      try {
        await axiosInstance.delete(`/client/tenant-clients/${id}`);
        navigate("/portal/clients");
      } catch (err) {
        setMessage({
          type: "error",
          text: err.response?.data?.message || "Could not deactivate client.",
        });
        setDeleting(false);
        setConfirmTarget(null);
      }
      return;
    }

    if (confirmTarget.kind === "service") {
      const service = confirmTarget.item;
      if (!service || service.Status === "Inactive") {
        setConfirmTarget(null);
        return;
      }
      setDeactivatingServiceId(service.Tenant_Client_Service_Id);
      setMessage({ type: "", text: "" });
      try {
        await axiosInstance.delete(
          `/client/tenant-clients/${id}/services/${service.Tenant_Client_Service_Id}`
        );
        await loadClient();
        setMessage({ type: "success", text: "Service deactivated." });
        setConfirmTarget(null);
      } catch (err) {
        setMessage({
          type: "error",
          text: err.response?.data?.message || "Could not deactivate service.",
        });
      } finally {
        setDeactivatingServiceId(null);
      }
      return;
    }

    if (confirmTarget.kind === "project") {
      const project = confirmTarget.item;
      if (!project || project.Status === "Inactive") {
        setConfirmTarget(null);
        return;
      }
      setDeactivatingProjectId(project.Tenant_Project_Id);
      setMessage({ type: "", text: "" });
      try {
        await axiosInstance.delete(
          `/client/tenant-clients/${id}/projects/${project.Tenant_Project_Id}`
        );
        await loadClient();
        setMessage({ type: "success", text: "Project deactivated." });
        setConfirmTarget(null);
      } catch (err) {
        setMessage({
          type: "error",
          text: err.response?.data?.message || "Could not deactivate project.",
        });
      } finally {
        setDeactivatingProjectId(null);
      }
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    if (!serviceForm.serviceName.trim()) {
      setServiceFormError("Service name is required.");
      return;
    }

    setServiceSubmitting(true);
    setServiceFormError("");
    try {
      if (editingServiceId) {
        await axiosInstance.put(
          `/client/tenant-clients/${id}/services/${editingServiceId}`,
          buildServicePayload(serviceForm)
        );
      } else {
        await axiosInstance.post(
          `/client/tenant-clients/${id}/services`,
          buildServicePayload(serviceForm)
        );
      }
      await loadClient();
      closeServiceModal();
      setMessage({ type: "success", text: editingServiceId ? "Service updated." : "Service added." });
    } catch (err) {
      setServiceFormError(err.response?.data?.message || "Could not save service.");
    } finally {
      setServiceSubmitting(false);
    }
  };

  const handleDeactivateService = (service) => {
    if (service.Status === "Inactive") return;
    setConfirmTarget({ kind: "service", item: service });
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    if (!projectForm.projectName.trim()) {
      setProjectFormError("Project name is required.");
      return;
    }
    if (!projectForm.shortCode.trim()) {
      setProjectFormError("Short code is required.");
      return;
    }
    const totalBeds = projectForm.totalBeds === "" ? null : Number.parseInt(projectForm.totalBeds, 10);
    const availableBeds =
      projectForm.availableBeds === "" ? null : Number.parseInt(projectForm.availableBeds, 10);
    if (projectForm.totalBeds !== "" && Number.isNaN(totalBeds)) {
      setProjectFormError("Total beds must be a valid number.");
      return;
    }
    if (projectForm.availableBeds !== "" && Number.isNaN(availableBeds)) {
      setProjectFormError("Available beds must be a valid number.");
      return;
    }
    if (totalBeds != null && availableBeds != null && availableBeds > totalBeds) {
      setProjectFormError("Available beds cannot be greater than total beds.");
      return;
    }

    setProjectSubmitting(true);
    setProjectFormError("");
    try {
      if (editingProjectId) {
        await axiosInstance.put(
          `/client/tenant-clients/${id}/projects/${editingProjectId}`,
          buildProjectPayload(projectForm)
        );
      } else {
        await axiosInstance.post(
          `/client/tenant-clients/${id}/projects`,
          buildProjectPayload(projectForm)
        );
      }
      await loadClient();
      closeProjectModal();
      setMessage({ type: "success", text: editingProjectId ? "Project updated." : "Project added." });
    } catch (err) {
      setProjectFormError(err.response?.data?.message || "Could not save project.");
    } finally {
      setProjectSubmitting(false);
    }
  };

  const handleDeactivateProject = (project) => {
    if (project.Status === "Inactive") return;
    setConfirmTarget({ kind: "project", item: project });
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => navigate("/portal/clients")}
        >
          <FaArrowLeft /> Back to clients
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Client not found."}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => {
            setEditing(false);
            setForm(toForm(client));
            setMessage({ type: "", text: "" });
          }}
        >
          <FaArrowLeft /> Back to client
        </button>

        <div className="cportal-page-head" style={{ marginTop: 8 }}>
          <h1>Edit Client</h1>
        </div>

        <form className="cportal-card" onSubmit={handleSave}>
          {message.text && (
            <PortalAlert
              type={message.type === "error" ? "error" : "success"}
              message={message.text}
              onClear={() => setMessage({ type: "", text: "" })}
            />
          )}
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>Client name <span className="cportal-required">*</span></label>
              <input
                value={form.clientName}
                onChange={(e) => setField("clientName", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>
                Contact person <span className="cportal-required">*</span>
              </label>
              <input
                value={form.contactPerson}
                onChange={(e) => setField("contactPerson", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>
                Mobile number <span className="cportal-required">*</span>
              </label>
              <input
                value={form.mobileNumber}
                onChange={(e) => setField("mobileNumber", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
                required
              />
            </div>
            <div className="cportal-field">
              <label>Alt number</label>
              <input
                value={form.altNumber}
                onChange={(e) => setField("altNumber", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <div className="cportal-field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Website</label>
              <input value={form.website} onChange={(e) => setField("website", e.target.value)} />
            </div>
            <PortalDomainSelect
              value={form.domain}
              onChange={(value) => setField("domain", value)}
              required
            />
            <div className="cportal-field">
              <label>
                Business Sub Type <span className="cportal-required">*</span>
              </label>
              <PortalSelect
                value={form.businessSubType}
                onChange={(e) => setField("businessSubType", e.target.value)}
              >
                <option value="">Select sub type</option>
                {BUSINESS_SUB_TYPES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </PortalSelect>
            </div>
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <PortalSelect value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {CLIENT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </PortalSelect>
            </div>
          </div>

          <div className="cportal-form-grid cportal-form-grid--3 cportal-form-grid--spaced-top">
            <PortalAddressFields form={form} setForm={setForm} />
            <div className="cportal-field cportal-field--full">
              <label>Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setField("description", e.target.value)} />
            </div>
          </div>

          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={() => {
                setEditing(false);
                setForm(toForm(client));
                setMessage({ type: "", text: "" });
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cportal-page">
      <button
        type="button"
        className="cportal-back-link"
        onClick={() => navigate("/portal/clients")}
      >
        <FaArrowLeft /> Back to clients
      </button>

      {message.text && (
        <PortalAlert
          type={message.type === "error" ? "error" : "success"}
          message={message.text}
          onClear={() => setMessage({ type: "", text: "" })}
        />
      )}

      <div className="cportal-entity-hero cportal-entity-hero--inline">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            {clientInitials(client.Client_Name)}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{displayValue(client.Client_Name)}</h1>
            <div className="cportal-entity-hero-sub">
              {String(client.Domain || "").trim() ? (
                <span className="cportal-company-hero-hint" title="Business Type">
                  {client.Domain}
                </span>
              ) : null}
              {String(client.Business_Sub_Type || "").trim() ? (
                <span className="cportal-company-hero-hint" title="Business Sub Type">
                  {client.Business_Sub_Type}
                </span>
              ) : null}
              <span className={statusBadgeClass(client.Status)}>
                {displayValue(client.Status)}
              </span>
            </div>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-edit"
            onClick={() => setEditing(true)}
            aria-label="Edit client"
            title="Edit client"
          >
            <FaEdit aria-hidden="true" /> <span className="cportal-btn-text">Edit</span>
          </button>
          {canDelete && (
            <button
              type="button"
              className="cportal-btn cportal-btn-danger-outline"
              onClick={handleDelete}
              disabled={deleting}
              aria-label="Delete client"
              title="Delete client"
            >
              <FaTrash aria-hidden="true" />{" "}
              <span className="cportal-btn-text">{deleting ? "Deactivating…" : "Delete"}</span>
            </button>
          )}
        </div>
      </div>

      <select
        className="cportal-section-select"
        value={activeSection}
        onChange={(e) => setActiveSection(e.target.value)}
        aria-label="Client section"
      >
        <option value="details">Details</option>
        {showProjectsTab ? <option value="projects">Projects</option> : null}
        {showServicesTab ? <option value="services">Services</option> : null}
      </select>
      <nav className="cportal-profile-nav" aria-label="Client sections">
        <button
          type="button"
          className={`cportal-profile-nav-link${activeSection === "details" ? " active" : ""}`}
          aria-current={activeSection === "details" ? "page" : undefined}
          onClick={() => setActiveSection("details")}
        >
          Details
        </button>
        {showProjectsTab ? (
          <button
            type="button"
            className={`cportal-profile-nav-link${activeSection === "projects" ? " active" : ""}`}
            aria-current={activeSection === "projects" ? "page" : undefined}
            onClick={() => setActiveSection("projects")}
          >
            Projects
          </button>
        ) : null}
        {showServicesTab ? (
          <button
            type="button"
            className={`cportal-profile-nav-link${activeSection === "services" ? " active" : ""}`}
            aria-current={activeSection === "services" ? "page" : undefined}
            onClick={() => setActiveSection("services")}
          >
            Services
          </button>
        ) : null}
      </nav>

      {activeSection === "details" && (
        <>
          <div className="cportal-detail-grid">
            <DetailSection icon={FaUser} title="Contact information">
              <div className="cportal-profile-view-grid">
                <DetailItem label="Client name" value={client.Client_Name} />
                <DetailItem label="Contact person" value={client.Contact_Person} />
                <DetailItem label="Mobile number">
                  <PhoneLink phone={client.Mobile_Number} />
                </DetailItem>
                <DetailItem label="Alt number">
                  <PhoneLink phone={client.Alt_Number} />
                </DetailItem>
                <DetailItem label="Email">
                  <EmailLink email={client.Email} />
                </DetailItem>
              </div>
            </DetailSection>

            <DetailSection icon={FaMapMarkerAlt} title="Address & business">
              <div className="cportal-profile-view-grid">
                <DetailItem
                  label="Address"
                  value={
                    joinParts([client.Address_Line_1, client.Address_Line_2], ", ") ||
                    client.Address
                  }
                />
                <DetailItem
                  label="City, State"
                  value={joinParts([client.City_Name, stateShortName(client.State_Name)], ", ")}
                />
                <DetailItem
                  label="Country, Zip Code"
                  value={joinParts(
                    [client.Country_Short_Code || client.Country_Name, client.Zip_Code],
                    " "
                  )}
                />
                <DetailItem label="Website">
                  <WebsiteLink url={client.Website} />
                </DetailItem>
                <DetailItem label="Business Type" value={client.Domain} />
                <DetailItem label="Business Sub Type" value={client.Business_Sub_Type} />
                <DetailItem label="Status" value={client.Status} />
              </div>
            </DetailSection>
          </div>

          <DetailSection icon={FaInfoCircle} title="Other details">
            <div className="cportal-profile-view-grid">
              <DetailItem label="Created on">
                <span className="cportal-datetime-value">
                  <FaCalendarAlt aria-hidden="true" />
                  {formatDateTime(client.Created_On)}
                </span>
              </DetailItem>
              <DetailItem label="Updated on">
                <span className="cportal-datetime-value">
                  <FaCalendarAlt aria-hidden="true" />
                  {formatDateTime(client.Updated_On)}
                </span>
              </DetailItem>
            </div>
          </DetailSection>

          <DetailSection icon={FaStickyNote} title="Description">
            <p className="cportal-notes-text">{String(client.Description || "").trim() || "—"}</p>
          </DetailSection>
        </>
      )}

      {activeSection === "projects" && (
        <>
          <DetailSection
            icon={FaProjectDiagram}
            title="Projects"
            className="cportal-detail-section--list"
            actions={
              <button type="button" className="cportal-btn cportal-btn-primary" onClick={openAddProjectModal}>
                Add project
              </button>
            }
          >
            {projects.length === 0 ? (
              <p className="cportal-muted">No projects added yet.</p>
            ) : (
              <>
              <div className="cportal-table-wrap co-desktop-table">
                <table className="cportal-table">
                  <thead>
                    <tr>
                      <th>Project name</th>
                      <th>Short code</th>
                      <th>Address</th>
                      <th>Total beds</th>
                      <th>Available beds</th>
                      <th>Rent range</th>
                      <th>Amenities</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th className="cportal-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.Tenant_Project_Id}>
                        <td>{displayValue(project.Project_Name)}</td>
                        <td>{displayValue(project.Short_Code)}</td>
                        <td>{displayValue(project.Address)}</td>
                        <td>{displayValue(project.Total_Beds)}</td>
                        <td>{displayValue(project.Available_Beds)}</td>
                        <td>{displayValue(project.Rent_Range)}</td>
                        <td>
                          {Array.isArray(project.Amenities) && project.Amenities.length > 0
                            ? project.Amenities.join(", ")
                            : "—"}
                        </td>
                        <td>{displayValue(project.Description)}</td>
                        <td>{displayValue(project.Status)}</td>
                        <td className="cportal-td-actions">
                          <div className="cportal-re-actions">
                            <button
                              type="button"
                              className="re-lead-action re-lead-action--view"
                              onClick={() =>
                                setAttachmentsTarget({
                                  type: "project",
                                  id: project.Tenant_Project_Id,
                                  name: project.Project_Name || "Project",
                                })
                              }
                              title="View attachments"
                              aria-label="View attachments"
                            >
                              <FaImages size={16} />
                            </button>
                            <button
                              type="button"
                              className="re-lead-action re-lead-action--edit"
                              onClick={() => openEditProjectModal(project)}
                              title="Edit project"
                              aria-label="Edit project"
                            >
                              <FaEdit size={16} />
                            </button>
                            {canDelete && (
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--delete"
                                onClick={() => handleDeactivateProject(project)}
                                disabled={
                                  project.Status === "Inactive" ||
                                  deactivatingProjectId === project.Tenant_Project_Id
                                }
                                title={
                                  project.Status === "Inactive"
                                    ? "Already inactive"
                                    : "Deactivate project"
                                }
                                aria-label="Deactivate project"
                              >
                                {deactivatingProjectId === project.Tenant_Project_Id ? "…" : <FaTrash size={16} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ResultsMobileList loading={false} emptyLabel="No projects added yet.">
                {projects.map((project) => {
                  const inactive = project.Status === "Inactive";
                  const busy = deactivatingProjectId === project.Tenant_Project_Id;
                  return (
                    <ResultsMobileCard
                      key={project.Tenant_Project_Id}
                      title={displayValue(project.Project_Name)}
                      subtitle={project.Short_Code || null}
                      meta={[
                        { label: displayValue(project.Status), tone: inactive ? "neutral" : "success", status: true },
                      ]}
                      fields={[
                        { label: "Address", value: project.Address },
                        { label: "Total beds", value: project.Total_Beds },
                        { label: "Available beds", value: project.Available_Beds },
                        { label: "Rent range", value: project.Rent_Range },
                        {
                          label: "Amenities",
                          value: Array.isArray(project.Amenities) && project.Amenities.length > 0 ? project.Amenities.join(", ") : null,
                        },
                        { label: "Description", value: project.Description },
                      ].filter((f) => f.value != null && String(f.value).trim() !== "")}
                      onOpen={() => openEditProjectModal(project)}
                      actions={
                        <div className="co-mcard__action-grid">
                          <button
                            type="button"
                            className="co-maction"
                            onClick={() =>
                              setAttachmentsTarget({
                                type: "project",
                                id: project.Tenant_Project_Id,
                                name: project.Project_Name || "Project",
                              })
                            }
                          >
                            <FaImages size={13} />
                            <span>Files</span>
                          </button>
                          <button type="button" className="co-maction" onClick={() => openEditProjectModal(project)}>
                            <FaEdit size={13} />
                            <span>Edit</span>
                          </button>
                          {canDelete ? (
                            <button
                              type="button"
                              className="co-maction co-maction--danger"
                              onClick={() => handleDeactivateProject(project)}
                              disabled={inactive || busy}
                            >
                              <FaTrash size={12} />
                              <span>{busy ? "…" : "Deactivate"}</span>
                            </button>
                          ) : null}
                        </div>
                      }
                    />
                  );
                })}
              </ResultsMobileList>
              </>
            )}
          </DetailSection>
        </>
      )}

      {activeSection === "services" && (
        <>
          <DetailSection
            icon={FaBriefcase}
            title="Services"
            className="cportal-detail-section--list"
            actions={
              <button type="button" className="cportal-btn cportal-btn-primary" onClick={openAddServiceModal}>
                Add service
              </button>
            }
          >
            {services.length === 0 ? (
              <p className="cportal-muted">No services added yet.</p>
            ) : (
              <>
              <div className="cportal-table-wrap co-desktop-table">
                <table className="cportal-table">
                  <thead>
                    <tr>
                      <th>Service name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th className="cportal-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service) => (
                      <tr key={service.Tenant_Client_Service_Id}>
                        <td>{displayValue(service.Service_Name)}</td>
                        <td>{displayValue(service.Description)}</td>
                        <td>{displayValue(service.Status)}</td>
                        <td className="cportal-td-actions">
                          <div className="cportal-re-actions">
                            <button
                              type="button"
                              className="re-lead-action re-lead-action--view"
                              onClick={() =>
                                setAttachmentsTarget({
                                  type: "service",
                                  id: service.Tenant_Client_Service_Id,
                                  name: service.Service_Name || "Service",
                                })
                              }
                              title="View attachments"
                              aria-label="View attachments"
                            >
                              <FaImages size={16} />
                            </button>
                            <button
                              type="button"
                              className="re-lead-action re-lead-action--edit"
                              onClick={() => openEditServiceModal(service)}
                              title="Edit service"
                              aria-label="Edit service"
                            >
                              <FaEdit size={16} />
                            </button>
                            {canDelete && (
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--delete"
                                onClick={() => handleDeactivateService(service)}
                                disabled={
                                  service.Status === "Inactive" ||
                                  deactivatingServiceId === service.Tenant_Client_Service_Id
                                }
                                title={
                                  service.Status === "Inactive"
                                    ? "Already inactive"
                                    : "Deactivate service"
                                }
                                aria-label="Deactivate service"
                              >
                                {deactivatingServiceId === service.Tenant_Client_Service_Id ? "…" : <FaTrash size={16} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ResultsMobileList loading={false} emptyLabel="No services added yet.">
                {services.map((service) => {
                  const inactive = service.Status === "Inactive";
                  const busy = deactivatingServiceId === service.Tenant_Client_Service_Id;
                  return (
                    <ResultsMobileCard
                      key={service.Tenant_Client_Service_Id}
                      title={displayValue(service.Service_Name)}
                      meta={[
                        { label: displayValue(service.Status), tone: inactive ? "neutral" : "success", status: true },
                      ]}
                      fields={
                        String(service.Description || "").trim()
                          ? [{ label: "Description", value: service.Description }]
                          : []
                      }
                      onOpen={() => openEditServiceModal(service)}
                      actions={
                        <div className="co-mcard__action-grid">
                          <button
                            type="button"
                            className="co-maction"
                            onClick={() =>
                              setAttachmentsTarget({
                                type: "service",
                                id: service.Tenant_Client_Service_Id,
                                name: service.Service_Name || "Service",
                              })
                            }
                          >
                            <FaImages size={13} />
                            <span>Files</span>
                          </button>
                          <button type="button" className="co-maction" onClick={() => openEditServiceModal(service)}>
                            <FaEdit size={13} />
                            <span>Edit</span>
                          </button>
                          {canDelete ? (
                            <button
                              type="button"
                              className="co-maction co-maction--danger"
                              onClick={() => handleDeactivateService(service)}
                              disabled={inactive || busy}
                            >
                              <FaTrash size={12} />
                              <span>{busy ? "…" : "Deactivate"}</span>
                            </button>
                          ) : null}
                        </div>
                      }
                    />
                  );
                })}
              </ResultsMobileList>
              </>
            )}
          </DetailSection>
        </>
      )}

      {showServiceModal && (
        <div className="cportal-modal-overlay" onClick={closeServiceModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="client-service-modal-title"
          >
            <div className="cportal-modal-header">
              <h3 id="client-service-modal-title">
                {editingServiceId ? "Edit Service" : "Add Service"}
              </h3>
              <button type="button" onClick={closeServiceModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleServiceSubmit}>
              <PortalAlert type="error" message={serviceFormError} onClear={() => setServiceFormError("")} />
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    Service name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={serviceForm.serviceName}
                    onChange={(e) => setServiceField("serviceName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                Status <span className="cportal-required">*</span>
              </label>
                  <PortalSelect
                    value={serviceForm.status}
                    onChange={(e) => setServiceField("status", e.target.value)}
                  >
                    {SERVICE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={serviceForm.description}
                    onChange={(e) => setServiceField("description", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeServiceModal}
                  disabled={serviceSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={serviceSubmitting}>
                  {serviceSubmitting ? "Saving…" : editingServiceId ? "Save service" : "Add service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showProjectModal && (
        <div className="cportal-modal-overlay" onClick={closeProjectModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="client-project-modal-title"
          >
            <div className="cportal-modal-header">
              <h3 id="client-project-modal-title">
                {editingProjectId ? "Edit Project" : "Add Project"}
              </h3>
              <button type="button" onClick={closeProjectModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleProjectSubmit}>
              <PortalAlert type="error" message={projectFormError} onClear={() => setProjectFormError("")} />
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    Project name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={projectForm.projectName}
                    onChange={(e) => setProjectField("projectName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Short code <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={projectForm.shortCode}
                    onChange={(e) => setProjectField("shortCode", e.target.value)}
                    maxLength={20}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                Status <span className="cportal-required">*</span>
              </label>
                  <PortalSelect
                    value={projectForm.status}
                    onChange={(e) => setProjectField("status", e.target.value)}
                  >
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Address</label>
                  <textarea
                    rows={2}
                    value={projectForm.address}
                    onChange={(e) => setProjectField("address", e.target.value)}
                    placeholder="Enter project address"
                    maxLength={500}
                  />
                </div>
                <div className="cportal-field">
                  <label>Total beds</label>
                  <input
                    type="number"
                    min="0"
                    value={projectForm.totalBeds}
                    onChange={(e) => setProjectField("totalBeds", e.target.value)}
                    placeholder="e.g. 100"
                  />
                </div>
                <div className="cportal-field">
                  <label>Available beds</label>
                  <input
                    type="number"
                    min="0"
                    value={projectForm.availableBeds}
                    onChange={(e) => setProjectField("availableBeds", e.target.value)}
                    placeholder="e.g. 25"
                  />
                </div>
                <div className="cportal-field">
                  <label>Rent range</label>
                  <input
                    type="text"
                    value={projectForm.rentRange}
                    onChange={(e) => setProjectField("rentRange", e.target.value)}
                    placeholder="e.g. 8000 - 15000"
                    maxLength={100}
                  />
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Project page link</label>
                  <input
                    type="url"
                    value={projectForm.projectUrl}
                    onChange={(e) => setProjectField("projectUrl", e.target.value)}
                    placeholder="https://your-site.com/project-details"
                    maxLength={1000}
                  />
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Amenities</label>
                  <div className="cportal-amenity-editor">
                    <div className="cportal-amenity-input-row">
                      <input
                        type="text"
                        value={projectForm.amenityDraft}
                        onChange={(e) => setProjectField("amenityDraft", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const next = String(projectForm.amenityDraft || "").trim();
                            if (!next) return;
                            const existing = Array.isArray(projectForm.amenities)
                              ? projectForm.amenities
                              : [];
                            if (
                              existing.some(
                                (item) => item.toLowerCase() === next.toLowerCase()
                              )
                            ) {
                              setProjectField("amenityDraft", "");
                              return;
                            }
                            setProjectForm((f) => ({
                              ...f,
                              amenities: [...(Array.isArray(f.amenities) ? f.amenities : []), next],
                              amenityDraft: "",
                            }));
                          }
                        }}
                        placeholder="Type an amenity and press Enter"
                      />
                      <button
                        type="button"
                        className="cportal-btn cportal-btn-ghost"
                        onClick={() => {
                          const next = String(projectForm.amenityDraft || "").trim();
                          if (!next) return;
                          const existing = Array.isArray(projectForm.amenities)
                            ? projectForm.amenities
                            : [];
                          if (
                            existing.some(
                              (item) => item.toLowerCase() === next.toLowerCase()
                            )
                          ) {
                            setProjectField("amenityDraft", "");
                            return;
                          }
                          setProjectForm((f) => ({
                            ...f,
                            amenities: [...(Array.isArray(f.amenities) ? f.amenities : []), next],
                            amenityDraft: "",
                          }));
                        }}
                      >
                        Add
                      </button>
                    </div>
                    {Array.isArray(projectForm.amenities) && projectForm.amenities.length > 0 && (
                      <div className="cportal-amenity-chips">
                        {projectForm.amenities.map((item, index) => (
                          <span key={`${item}-${index}`} className="cportal-amenity-chip">
                            {item}
                            <button
                              type="button"
                              aria-label={`Remove ${item}`}
                              onClick={() =>
                                setProjectForm((f) => ({
                                  ...f,
                                  amenities: (Array.isArray(f.amenities) ? f.amenities : []).filter(
                                    (_, i) => i !== index
                                  ),
                                }))
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={projectForm.description}
                    onChange={(e) => setProjectField("description", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeProjectModal}
                  disabled={projectSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={projectSubmitting}>
                  {projectSubmitting ? "Saving…" : editingProjectId ? "Save project" : "Add project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <PortalEntityAttachmentsModal
        open={!!attachmentsTarget}
        onClose={() => setAttachmentsTarget(null)}
        tenantClientId={id}
        entityType={attachmentsTarget?.type || "service"}
        entityId={attachmentsTarget?.id}
        entityName={attachmentsTarget?.name}
      />

      <PortalConfirmModal
        open={!!confirmTarget}
        title={
          confirmTarget?.kind === "client"
            ? "Deactivate Client"
            : confirmTarget?.kind === "service"
              ? "Deactivate Service"
              : "Deactivate Project"
        }
        message={
          confirmTarget?.kind === "client" ? (
            <p>
              Deactivate <strong>{client?.Client_Name}</strong>? Their status will be set to
              Inactive.
            </p>
          ) : confirmTarget?.kind === "service" ? (
            <p>
              Deactivate <strong>{confirmTarget.item?.Service_Name}</strong>? Their status will be
              set to Inactive.
            </p>
          ) : confirmTarget?.kind === "project" ? (
            <p>
              Deactivate <strong>{confirmTarget.item?.Project_Name}</strong>? Their status will be
              set to Inactive.
            </p>
          ) : null
        }
        confirming={
          deleting || !!deactivatingServiceId || !!deactivatingProjectId
        }
        confirmingLabel={
          confirmTarget?.kind === "client" ? "Deactivating…" : "Deactivating…"
        }
        onConfirm={confirmDestructive}
        onClose={closeConfirm}
      />
    </div>
  );
}

export default TenantClientView;
